import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientHistoryService } from '../services/patientHistoryService'
import type { PatientClinicalHistory as PatientClinicalHistoryType } from '../types/patientHistory'
import { PatientCondition, FamilyHistory } from '../types/patientHistory'
import { ConditionsList } from './ConditionsList'
import { FamilyHistoryList } from './FamilyHistoryList'
import { PatientPreferences } from './PatientPreferences'
import { ConsentManagement } from './ConsentManagement'
import { Modal } from './Modal'
import { ConditionForm } from './ConditionForm'
import { FamilyHistoryForm } from './FamilyHistoryForm'
import './PatientClinicalHistory.css'

interface PatientClinicalHistoryProps {
  patientId: string
  initialHistory?: PatientClinicalHistoryType | null
}

export function PatientClinicalHistory({ patientId, initialHistory }: PatientClinicalHistoryProps) {
  const queryClient = useQueryClient()
  const [showConditionForm, setShowConditionForm] = useState(false)
  const [showFamilyHistoryForm, setShowFamilyHistoryForm] = useState(false)
  const [editingCondition, setEditingCondition] = useState<PatientCondition | null>(null)
  const [editingFamilyHistory, setEditingFamilyHistory] = useState<FamilyHistory | null>(null)

  const hasInitialHistory = initialHistory !== undefined
  const { data: history, isLoading } = useQuery<PatientClinicalHistoryType | null>({
    queryKey: ['patient-clinical-history', patientId],
    queryFn: () => patientHistoryService.getClinicalHistory(patientId),
    enabled: !!patientId && !hasInitialHistory,
    initialData: initialHistory,
  })

  const createHistoryMutation = useMutation({
    mutationFn: () => patientHistoryService.saveClinicalHistory(patientId, {
      conditions: [],
      familyHistory: [],
      preferences: {
        recordedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      consents: [],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
    },
  })

  if (isLoading) {
    return <div className="clinical-history-loading">Loading patient history...</div>
  }

  // Initialize history if it doesn't exist
  if (!history && !isLoading) {
    return (
      <div className="clinical-history-empty">
        <p>No clinical history recorded yet for this patient.</p>
        <button
          className="btn-primary"
          onClick={() => createHistoryMutation.mutate()}
          disabled={createHistoryMutation.isPending}
        >
          {createHistoryMutation.isPending ? 'Initializing...' : 'Initialize Clinical History'}
        </button>
      </div>
    )
  }

  if (!history) return null

  return (
    <div className="patient-clinical-history">
      <div className="clinical-history-section">
        <div className="section-header">
          <h2>Clinical Conditions</h2>
          <button
            className="btn-primary btn-small"
            onClick={() => {
              setEditingCondition(null)
              setShowConditionForm(true)
            }}
          >
            + Add Condition
          </button>
        </div>
        <ConditionsList
          conditions={history.conditions}
          patientId={patientId}
          onEdit={(condition) => {
            setEditingCondition(condition)
            setShowConditionForm(true)
          }}
          onDelete={async (conditionId) => {
            await patientHistoryService.removeCondition(patientId, conditionId)
            queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
          }}
        />
      </div>

      <div className="clinical-history-section">
        <div className="section-header">
          <h2>Family History</h2>
          <button
            className="btn-primary btn-small"
            onClick={() => {
              setEditingFamilyHistory(null)
              setShowFamilyHistoryForm(true)
            }}
          >
            + Add Family History
          </button>
        </div>
        <FamilyHistoryList
          familyHistory={history.familyHistory}
          patientId={patientId}
          onEdit={(entry) => {
            setEditingFamilyHistory(entry)
            setShowFamilyHistoryForm(true)
          }}
          onDelete={async (entryId) => {
            await patientHistoryService.removeFamilyHistory(patientId, entryId)
            queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
          }}
        />
      </div>

      <div className="clinical-history-section">
        <div className="section-header">
          <h2>Patient Preferences</h2>
        </div>
        <PatientPreferences
          patientId={patientId}
          preferences={history.preferences}
        />
      </div>

      <div className="clinical-history-section">
        <div className="section-header">
          <h2>Consent & Authority</h2>
          <button
            className="btn-primary btn-small"
            onClick={() => {
              // Consent form will be handled by ConsentManagement component
            }}
          >
            + Add Consent
          </button>
        </div>
        <ConsentManagement
          patientId={patientId}
          consents={history.consents}
        />
      </div>

      {showConditionForm && (
        <Modal
          isOpen={showConditionForm}
          onClose={() => {
            setShowConditionForm(false)
            setEditingCondition(null)
          }}
          title={editingCondition ? 'Edit Condition' : 'Add Condition'}
          size="medium"
        >
          <ConditionForm
            patientId={patientId}
            initialData={editingCondition || undefined}
            onSuccess={() => {
              setShowConditionForm(false)
              setEditingCondition(null)
              queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
            }}
            onCancel={() => {
              setShowConditionForm(false)
              setEditingCondition(null)
            }}
          />
        </Modal>
      )}

      {showFamilyHistoryForm && (
        <Modal
          isOpen={showFamilyHistoryForm}
          onClose={() => {
            setShowFamilyHistoryForm(false)
            setEditingFamilyHistory(null)
          }}
          title={editingFamilyHistory ? 'Edit Family History' : 'Add Family History'}
          size="medium"
        >
          <FamilyHistoryForm
            patientId={patientId}
            initialData={editingFamilyHistory || undefined}
            onSuccess={() => {
              setShowFamilyHistoryForm(false)
              setEditingFamilyHistory(null)
              queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
            }}
            onCancel={() => {
              setShowFamilyHistoryForm(false)
              setEditingFamilyHistory(null)
            }}
          />
        </Modal>
      )}
    </div>
  )
}
